import { createSlice, createSelector } from '@reduxjs/toolkit'
import {
  isOverdueInUserTimezone,
  isTodayInUserTimezone,
  parseUTCToUserTimezone,
  getTodayInUserTimezone,
  isOverdue,
  isToday
} from '../utils/dateUtils'
import { ATTENTION_THRESHOLDS, getAttentionThreshold } from '../utils/taskDisplayUtils'
import { DEFAULT_TIMEZONE } from '../utils/constants'

// Import modular reducers and thunks
import {
  fetchTasks,
  createTask,
  updateTask,
  deleteTask,
  archiveTask,
  unarchiveTask,
  bulkArchiveTasks,
  reorderTasksInColumn,
  updateTaskPlanOptimistic,
  tasksReducers
} from './reducers/tasksReducers'

import {
  fetchTaskStatuses,
  createTaskStatus,
  updateTaskStatus,
  deleteTaskStatus,
  fetchBoardColumns,
  fetchColumnStatusMappings,
  initializeBoardSetup,
  createBoardColumn,
  updateBoardColumn,
  deleteBoardColumn,
  updateBoardColumns,
  createColumnStatusMapping,
  deleteColumnStatusMapping,
  statusReducers,
  statusRegularReducers
} from './reducers/statusReducers'

import { 
  fetchProjects, 
  createProject, 
  updateProject, 
  deleteProject, 
  updateProjectOrder,
  projectsReducers,
  projectsRegularReducers
} from './reducers/projectsReducers'

import {
  initializeRealtimeSubscriptions,
  cleanupRealtimeSubscriptions,
  handleRealtimeTaskEvent,
  handleRealtimeProjectEvent,
  handleRealtimeTaskStatusEvent,
  handleRealtimeBoardColumnEvent,
  handleRealtimeColumnMappingEvent,
  handleRealtimeLabelEvent,
  handleRealtimeLabelAssignmentEvent,
  realtimeReducers,
  realtimeRegularReducers
} from './reducers/realtimeReducers'

// Initial state following Redux-first pattern with unified data
const initialState = {
  tasks: [],
  projects: [], // Moved from separate projectsSlice
  taskStatuses: [],
  boardColumns: [],
  columnStatusMappings: [],
  labelAssignments: {}, // Cache of label assignments: { assignmentId: { task_id, label_id } }
  loading: {
    tasks: false,
    projects: false, // Now managed in unified slice
    taskStatuses: false,
    boardColumns: false,
    columnStatusMappings: false
  },
  error: {
    tasks: null,
    projects: null, // Now managed in unified slice
    taskStatuses: null,
    boardColumns: null,
    columnStatusMappings: null
  },
  initialized: {
    tasks: false,
    projects: false, // Now properly tracked in same slice!
    taskStatuses: false,
    boardColumns: false,
    columnStatusMappings: false
  },
  // Realtime state management
  realtime: {
    initializing: false,
    initialized: false,
    subscriptions: [],
    connectionState: 'DISCONNECTED', // DISCONNECTED, CONNECTED, ERROR
    error: null
  }
}


// Helper function to hydrate label assignments cache from tasks
// This allows DELETE realtime events to work even after page refresh
const hydrateLabelAssignmentsCache = (state, tasks) => {
  if (!state.labelAssignments) {
    state.labelAssignments = {}
  }

  tasks.forEach(task => {
    if (task.labels && Array.isArray(task.labels)) {
      task.labels.forEach(label => {
        if (label.assignment_id) {
          state.labelAssignments[label.assignment_id] = {
            task_id: task.id,
            label_id: label.id
          }
        } else if (import.meta.env.DEV) {
          console.warn('[Tasks] Missing assignment_id in cache hydration:', {
            taskId: task.id,
            labelId: label.id,
            labelName: label.name
          })
        }
      })
    }
  })
}

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setTasks: (state, action) => {
      state.tasks = action.payload
      state.initialized.tasks = true
      hydrateLabelAssignmentsCache(state, action.payload)
    },
    setProjects: (state, action) => {
      state.projects = action.payload
      state.initialized.projects = true
    },
    setTaskStatuses: (state, action) => {
      state.taskStatuses = action.payload
      state.initialized.taskStatuses = true
    },
    setBoardColumns: (state, action) => {
      state.boardColumns = action.payload
      state.initialized.boardColumns = true
    },
    setColumnStatusMappings: (state, action) => {
      state.columnStatusMappings = action.payload
      state.initialized.columnStatusMappings = true
    },
    clearTasks: (state) => {
      state.tasks = []
      state.taskStatuses = []
      state.projects = []
      state.labelAssignments = {}  // Clear label assignment cache to prevent memory leaks
      state.initialized.tasks = false
      state.initialized.taskStatuses = false
      state.initialized.projects = false
      // Reset realtime state
      state.realtime = {
        initializing: false,
        initialized: false,
        subscriptions: [],
        connectionState: 'DISCONNECTED',
        error: null
      }
    },
    clearError: (state, action) => {
      const { dataType } = action.payload
      if (state.error[dataType]) {
        state.error[dataType] = null
      }
    },
    // Include projects, statuses, and realtime regular reducers
    // NOTE: tasksRegularReducers moved to extraReducers to fix action type mismatch
    ...projectsRegularReducers,
    ...statusRegularReducers,
    ...realtimeRegularReducers,
    
    // Optimistic update reducers for task plans
    updateTaskPlanOptimistic: (state, action) => {
      const { taskId, planUpdates } = action.payload
      const taskIndex = state.tasks.findIndex(task => task.id === taskId)
      if (taskIndex !== -1) {
        state.tasks[taskIndex].plan = {
          ...state.tasks[taskIndex].plan,
          ...planUpdates,
          metadata: {
            ...state.tasks[taskIndex].plan?.metadata,
            updated_at: new Date().toISOString()
          }
        }
      }
    },

    // Rollback optimistic updates on API failure
    rollbackTaskPlanUpdate: (state, action) => {
      const { taskId, previousPlan } = action.payload
      const taskIndex = state.tasks.findIndex(task => task.id === taskId)
      if (taskIndex !== -1) {
        state.tasks[taskIndex].plan = previousPlan
      }
    },

    // Label handling reducers
    updateTaskLabels: (state, action) => {
      const { taskId, labels } = action.payload
      const taskIndex = state.tasks.findIndex(task => task.id === taskId)
      if (taskIndex !== -1) {
        state.tasks[taskIndex].labels = labels
      }
    },

    // Update all tasks when a label is modified
    updateLabelInTasks: (state, action) => {
      const updatedLabel = action.payload
      state.tasks.forEach(task => {
        if (task.labels && task.labels.length > 0) {
          const labelIndex = task.labels.findIndex(label => label.id === updatedLabel.id)
          if (labelIndex !== -1) {
            task.labels[labelIndex] = updatedLabel
          }
        }
      })
    },

    // Remove label from all tasks when label is deleted
    removeLabelFromTasks: (state, action) => {
      const labelId = action.payload
      state.tasks.forEach(task => {
        if (task.labels && task.labels.length > 0) {
          task.labels = task.labels.filter(label => label.id !== labelId)
        }
      })
    }
    
  },
  extraReducers: (builder) => {
    builder
      // Tasks reducers from modular file
      .addCase(fetchTasks.pending, tasksReducers[fetchTasks.pending])
      .addCase(fetchTasks.fulfilled, tasksReducers[fetchTasks.fulfilled])
      .addCase(fetchTasks.rejected, tasksReducers[fetchTasks.rejected])
      .addCase(createTask.fulfilled, tasksReducers[createTask.fulfilled])
      .addCase(updateTask.fulfilled, tasksReducers[updateTask.fulfilled])
      .addCase(deleteTask.fulfilled, tasksReducers[deleteTask.fulfilled])
      .addCase(archiveTask.fulfilled, tasksReducers[archiveTask.fulfilled])
      .addCase(unarchiveTask.fulfilled, tasksReducers[unarchiveTask.fulfilled])
      .addCase(bulkArchiveTasks.fulfilled, tasksReducers[bulkArchiveTasks.fulfilled])
      .addCase(reorderTasksInColumn.fulfilled, tasksReducers[reorderTasksInColumn.fulfilled])
      .addCase(reorderTasksInColumn.rejected, tasksReducers[reorderTasksInColumn.rejected])

      // Status and Board reducers from modular file
      .addCase(fetchTaskStatuses.pending, statusReducers[fetchTaskStatuses.pending])
      .addCase(fetchTaskStatuses.fulfilled, statusReducers[fetchTaskStatuses.fulfilled])
      .addCase(fetchTaskStatuses.rejected, statusReducers[fetchTaskStatuses.rejected])

      // Task Status CRUD operations
      .addCase(createTaskStatus.pending, statusReducers[createTaskStatus.pending])
      .addCase(createTaskStatus.fulfilled, statusReducers[createTaskStatus.fulfilled])
      .addCase(createTaskStatus.rejected, statusReducers[createTaskStatus.rejected])
      .addCase(updateTaskStatus.pending, statusReducers[updateTaskStatus.pending])
      .addCase(updateTaskStatus.fulfilled, statusReducers[updateTaskStatus.fulfilled])
      .addCase(updateTaskStatus.rejected, statusReducers[updateTaskStatus.rejected])
      .addCase(deleteTaskStatus.pending, statusReducers[deleteTaskStatus.pending])
      .addCase(deleteTaskStatus.fulfilled, statusReducers[deleteTaskStatus.fulfilled])
      .addCase(deleteTaskStatus.rejected, statusReducers[deleteTaskStatus.rejected])

      .addCase(fetchBoardColumns.pending, statusReducers[fetchBoardColumns.pending])
      .addCase(fetchBoardColumns.fulfilled, statusReducers[fetchBoardColumns.fulfilled])
      .addCase(fetchBoardColumns.rejected, statusReducers[fetchBoardColumns.rejected])
      .addCase(fetchColumnStatusMappings.pending, statusReducers[fetchColumnStatusMappings.pending])
      .addCase(fetchColumnStatusMappings.fulfilled, statusReducers[fetchColumnStatusMappings.fulfilled])
      .addCase(fetchColumnStatusMappings.rejected, statusReducers[fetchColumnStatusMappings.rejected])
      .addCase(initializeBoardSetup.fulfilled, statusReducers[initializeBoardSetup.fulfilled])
      .addCase(initializeBoardSetup.rejected, statusReducers[initializeBoardSetup.rejected])

      // Board Column Management reducers
      .addCase(createBoardColumn.fulfilled, statusReducers[createBoardColumn.fulfilled])
      .addCase(updateBoardColumn.fulfilled, statusReducers[updateBoardColumn.fulfilled])
      .addCase(deleteBoardColumn.fulfilled, statusReducers[deleteBoardColumn.fulfilled])
      .addCase(updateBoardColumns.fulfilled, statusReducers[updateBoardColumns.fulfilled])

      // Column Status Mapping Management reducers
      .addCase(createColumnStatusMapping.fulfilled, statusReducers[createColumnStatusMapping.fulfilled])
      .addCase(deleteColumnStatusMapping.fulfilled, statusReducers[deleteColumnStatusMapping.fulfilled])


      // Projects reducers from modular file
      .addCase(fetchProjects.pending, projectsReducers[fetchProjects.pending])
      .addCase(fetchProjects.fulfilled, projectsReducers[fetchProjects.fulfilled])
      .addCase(fetchProjects.rejected, projectsReducers[fetchProjects.rejected])
      .addCase(createProject.pending, projectsReducers[createProject.pending])
      .addCase(createProject.fulfilled, projectsReducers[createProject.fulfilled])
      .addCase(createProject.rejected, projectsReducers[createProject.rejected])
      .addCase(updateProject.pending, projectsReducers[updateProject.pending])
      .addCase(updateProject.fulfilled, projectsReducers[updateProject.fulfilled])
      .addCase(updateProject.rejected, projectsReducers[updateProject.rejected])
      .addCase(deleteProject.pending, projectsReducers[deleteProject.pending])
      .addCase(deleteProject.fulfilled, projectsReducers[deleteProject.fulfilled])
      .addCase(deleteProject.rejected, projectsReducers[deleteProject.rejected])
      .addCase(updateProjectOrder.fulfilled, projectsReducers[updateProjectOrder.fulfilled])
      .addCase(updateProjectOrder.rejected, projectsReducers[updateProjectOrder.rejected])

      // Realtime reducers from modular file
      .addCase(initializeRealtimeSubscriptions.pending, realtimeReducers[initializeRealtimeSubscriptions.pending])
      .addCase(initializeRealtimeSubscriptions.fulfilled, realtimeReducers[initializeRealtimeSubscriptions.fulfilled])
      .addCase(initializeRealtimeSubscriptions.rejected, realtimeReducers[initializeRealtimeSubscriptions.rejected])
      .addCase(cleanupRealtimeSubscriptions.fulfilled, realtimeReducers[cleanupRealtimeSubscriptions.fulfilled])
      .addCase(handleRealtimeTaskEvent.fulfilled, realtimeReducers[handleRealtimeTaskEvent.fulfilled])
      .addCase(handleRealtimeProjectEvent.fulfilled, realtimeReducers[handleRealtimeProjectEvent.fulfilled])
      .addCase(handleRealtimeTaskStatusEvent.fulfilled, realtimeReducers[handleRealtimeTaskStatusEvent.fulfilled])
      .addCase(handleRealtimeBoardColumnEvent.fulfilled, realtimeReducers[handleRealtimeBoardColumnEvent.fulfilled])
      .addCase(handleRealtimeColumnMappingEvent.fulfilled, realtimeReducers[handleRealtimeColumnMappingEvent.fulfilled])
      .addCase(handleRealtimeLabelEvent.fulfilled, realtimeReducers[handleRealtimeLabelEvent.fulfilled])
      .addCase(handleRealtimeLabelAssignmentEvent.fulfilled, realtimeReducers[handleRealtimeLabelAssignmentEvent.fulfilled])

      // Optimistic update reducers for tasks - using addMatcher for exact action type matching
      // These were previously in reducers object but action types were double-prefixed
      .addMatcher(
        (action) => action.type === 'tasks/createTask/optimistic',
        (state, action) => {
          state.tasks.unshift(action.payload)
        }
      )
      .addMatcher(
        (action) => action.type === 'tasks/createTask/rollback',
        (state, action) => {
          state.tasks = state.tasks.filter(task => task.id !== action.payload)
        }
      )
      .addMatcher(
        (action) => action.type === 'tasks/updateTask/optimistic',
        (state, action) => {
          const index = state.tasks.findIndex(task => task.id === action.payload.task.id)
          if (index !== -1) {
            state.tasks[index] = action.payload.task
          }
        }
      )
      .addMatcher(
        (action) => action.type === 'tasks/updateTask/rollback',
        (state, action) => {
          const index = state.tasks.findIndex(task => task.id === action.payload.taskId)
          if (index !== -1) {
            state.tasks[index] = action.payload.originalTask
          }
        }
      )
      .addMatcher(
        (action) => action.type === 'tasks/reorderTasksInColumn/optimistic',
        (state, action) => {
          const { statusId, reorderedTasks } = action.payload

          // Find all tasks that are NOT being reordered in this column
          const otherTasks = state.tasks.filter(task =>
            task.status_id !== statusId || !reorderedTasks.some(rt => rt.id === task.id)
          )

          // Create properly ordered tasks with preserved data
          const orderedTasksForColumn = reorderedTasks.map((reorderedTask, index) => {
            // Find the original task to preserve all its data
            const originalTask = state.tasks.find(t => t.id === reorderedTask.id)
            return {
              ...originalTask,
              ...reorderedTask,
              order_position: index + 1,
              updated_at: new Date().toISOString()
            }
          })

          // Combine all tasks: other tasks + reordered tasks
          state.tasks = [...otherTasks, ...orderedTasksForColumn]
        }
      )
      .addMatcher(
        (action) => action.type === 'tasks/reorderTasksInColumn/rollback',
        (state, action) => {
          const { originalTasks } = action.payload
          state.tasks = originalTasks
        }
      )
  }
})

// Export all thunks from modular reducers
export {
  // Task thunks
  fetchTasks, createTask, updateTask, deleteTask, archiveTask, unarchiveTask, bulkArchiveTasks,
  // Status and Board thunks
  fetchTaskStatuses, createTaskStatus, updateTaskStatus, deleteTaskStatus,
  fetchBoardColumns, fetchColumnStatusMappings, initializeBoardSetup,
  // Board management thunks
  createBoardColumn, updateBoardColumn, deleteBoardColumn, updateBoardColumns,
  createColumnStatusMapping, deleteColumnStatusMapping,
  // Board operation thunks
  reorderTasksInColumn,
  // Plan update thunks
  updateTaskPlanOptimistic,
  // Project thunks
  fetchProjects, createProject, updateProject, deleteProject, updateProjectOrder,
  // Realtime thunks
  initializeRealtimeSubscriptions, cleanupRealtimeSubscriptions,
  handleRealtimeTaskEvent, handleRealtimeProjectEvent, handleRealtimeTaskStatusEvent,
  handleRealtimeBoardColumnEvent, handleRealtimeColumnMappingEvent
}

export const {
  setTasks,
  setProjects,
  setTaskStatuses,
  setBoardColumns,
  setColumnStatusMappings,
  clearTasks,
  clearError,
  setRealtimeConnectionState,
  setRealtimeError,
  clearRealtimeError,
  rollbackTaskPlanUpdate,
  updateTaskLabels,
  updateLabelInTasks,
  removeLabelFromTasks,
  updateTaskStatusOptimistic,
  rollbackTaskStatusUpdate
} = tasksSlice.actions

// Selectors
export const selectTasks = (state) => state.tasks.tasks
export const selectProjects = (state) => state.tasks.projects  // Now from unified slice
export const selectTaskStatuses = (state) => state.tasks.taskStatuses
export const selectTasksLoading = (state) => state.tasks.loading
export const selectTasksError = (state) => state.tasks.error
export const selectTasksInitialized = (state) => state.tasks.initialized

export const selectTasksByProject = createSelector(
  [selectTasks, (state, projectId) => projectId],
  (tasks, projectId) => tasks.filter(task => task.project_id === projectId)
)

export const selectTasksByWeeklyGoal = createSelector(
  [selectTasks, (state, weeklyGoalId) => weeklyGoalId],
  (tasks, weeklyGoalId) => tasks.filter(task => task.weekly_goal_id === weeklyGoalId)
)

export const selectTasksByLabel = createSelector(
  [selectTasks, (state, labelId) => labelId],
  (tasks, labelId) => tasks.filter(task =>
    task.labels && task.labels.some(label => label.id === labelId)
  )
)

export const selectNonArchivedTasks = createSelector(
  [selectTasks],
  (tasks) => tasks.filter(task => !task.archived)
)

export const selectArchivedTasks = createSelector(
  [selectTasks],
  (tasks) => tasks.filter(task => task.archived)
)

export const selectCompletedTasksForArchiving = createSelector(
  [selectTasks],
  (tasks) => tasks.filter(task => task.completed && !task.archived)
)

export const selectCompletedTasksForArchivingByContext = createSelector(
  [
    selectCompletedTasksForArchiving,
    (state, contextType) => contextType,
    (state, contextType, contextId) => contextId
  ],
  (tasks, contextType, contextId) => {
    if (contextType === 'project') {
      return tasks.filter(task => task.project_id === contextId)
    } else if (contextType === 'weekly_goal') {
      return tasks.filter(task => task.weekly_goal_id === contextId)
    }
    return tasks // All tasks for 'all_tasks' context
  }
)

// Memoized selector for "All Tasks" view with project and weekly goal status filtering
export const selectAllTasksFilteredByActiveContext = createSelector(
  [selectNonArchivedTasks, selectProjects, state => state.goals.weeklyGoals],
  (tasks, projects, weeklyGoals) => {
    // Create lookup Maps for O(1) access instead of O(n) with .find()
    const projectMap = new Map(projects.map(p => [p.id, p]))
    const weeklyGoalMap = new Map(weeklyGoals.map(wg => [wg.id, wg]))

    return tasks.filter(task => {
      // Case 1: Task has a project_id
      if (task.project_id) {
        // Only include if project exists and status === 'active'
        // Tasks with deleted/missing projects are excluded
        const project = projectMap.get(task.project_id)
        return project && project.status === 'active'
      }

      // Case 2: Task has a weekly_goal_id (no project_id)
      if (task.weekly_goal_id) {
        // Only include if weekly goal exists and status === 'scheduled'
        // Tasks with deleted/missing weekly goals are excluded
        const weeklyGoal = weeklyGoalMap.get(task.weekly_goal_id)
        return weeklyGoal && weeklyGoal.status === 'scheduled'
      }

      // Case 3: Inbox task (no project_id, no weekly_goal_id)
      return true
    })
  }
)

export const selectUnassignedTasks = createSelector(
  [selectTasks],
  (tasks) => tasks.filter(task => !task.project_id && !task.weekly_goal_id && !task.archived)
)

// Non-archived context selectors (memoized for performance)
export const selectNonArchivedTasksByProject = createSelector(
  [selectTasksByProject],
  (tasks) => tasks.filter(task => !task.archived)
)

export const selectNonArchivedTasksByWeeklyGoal = createSelector(
  [selectTasksByWeeklyGoal],
  (tasks) => tasks.filter(task => !task.archived)
)

export const selectNonArchivedTasksByLabel = createSelector(
  [selectTasksByLabel],
  (tasks) => tasks.filter(task => !task.archived)
)

// Board selectors
export const selectBoardColumns = (state) => state.tasks.boardColumns
export const selectColumnStatusMappings = (state) => state.tasks.columnStatusMappings

// Check if user has board setup (has statuses and columns)
export const selectHasBoardSetup = createSelector(
  [selectTaskStatuses, selectBoardColumns],
  (statuses, columns) => statuses.length > 0 && columns.length > 0
)

// Enhanced board data with column-task mapping and proper ordering
export const selectBoardData = createSelector(
  [selectBoardColumns, selectTaskStatuses, selectTasks],
  (columns, statuses, tasks) => {
    return columns.map(column => {
      // Each column now has a single status_id (not an array)
      const statusId = column.status_id

      // Get tasks for this status and sort them properly by order_position
      const columnTasks = tasks
        .filter(task => task.status_id && task.status_id === statusId)
        .sort((a, b) => {
          // First sort by order_position (ascending, handling nulls)
          const posA = a.order_position || 999999
          const posB = b.order_position || 999999
          if (posA !== posB) return posA - posB

          // If positions are equal, sort by created_at (newest first as fallback)
          return new Date(b.created_at) - new Date(a.created_at)
        })

      // Get status info for this column (single status)
      const columnStatus = statuses.find(status => status.id === statusId)

      return {
        ...column,
        status: columnStatus, // Single status object (not array)
        tasks: columnTasks,
        taskCount: columnTasks.length
      }
    })
  }
)

// Enhanced attention selectors that work directly with main tasks array
const selectTasksWithAttentionAnalysis = createSelector(
  [selectNonArchivedTasks, (state, userTimezone) => userTimezone, (state, userTimezone, userSettings) => userSettings],
  (tasks, userTimezone, userSettings) => {
    const now = new Date()
    const todayInUserTZ = getTodayInUserTimezone(userTimezone)
    
    return tasks.map(task => {
      // Calculate days since creation using timezone-aware approach
      const createdDateInUserTZ = parseUTCToUserTimezone(task.created_at, userTimezone)
      const daysSinceCreation = createdDateInUserTZ 
        ? Math.floor((todayInUserTZ - createdDateInUserTZ) / (1000 * 60 * 60 * 24))
        : 0

      // Check if due_date is a date string (YYYY-MM-DD) or UTC timestamp
      const isDueDateString = task.due_date && !task.due_date.includes('T')
      
      let taskIsOverdue = false
      let taskIsDueToday = false
      
      if (task.due_date) {
        if (isDueDateString) {
          // Use simple date string comparison functions for YYYY-MM-DD format
          taskIsDueToday = isToday(task.due_date)
          taskIsOverdue = isOverdue(task.due_date) && !taskIsDueToday && !task.completed
        } else {
          // Use timezone-aware functions for UTC timestamps
          taskIsDueToday = isTodayInUserTimezone(task.due_date, userTimezone)
          taskIsOverdue = isOverdueInUserTimezone(task.due_date, userTimezone) && !taskIsDueToday && !task.completed
        }
      }

      // Calculate days until due using timezone-aware approach
      let daysUntilDue = null
      if (task.due_date) {
        // Check if due_date is a date string (YYYY-MM-DD) or UTC timestamp
        const isDueDateString = task.due_date && !task.due_date.includes('T')
        
        if (isDueDateString) {
          // For date strings, use simple date parsing
          const dueDate = new Date(task.due_date + 'T00:00:00.000')
          dueDate.setHours(0, 0, 0, 0)
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          daysUntilDue = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24))
        } else {
          // For UTC timestamps, use timezone-aware parsing
          const dueDateInUserTZ = parseUTCToUserTimezone(task.due_date, userTimezone)
          if (dueDateInUserTZ) {
            daysUntilDue = Math.floor((dueDateInUserTZ - todayInUserTZ) / (1000 * 60 * 60 * 24))
          }
        }
      }

      // Calculate time in current status (simplified - using updated_at)
      let hoursInCurrentStatus = null
      if (task.updated_at) {
        const taskUpdatedDate = new Date(task.updated_at)
        hoursInCurrentStatus = (now - taskUpdatedDate) / (1000 * 60 * 60)
      } else {
        const taskCreatedDate = new Date(task.created_at)
        hoursInCurrentStatus = (now - taskCreatedDate) / (1000 * 60 * 60)
      }

      // Determine attention categories with proper exclusions
      const attentionFlags = {
        // Overdue: past due date but NOT due today (ensure mutual exclusion)
        overdue: taskIsOverdue && !taskIsDueToday && !task.completed && !task.archived,
        dueToday: taskIsDueToday && !task.completed && !task.archived,
        dueSoon: task.due_date && !taskIsOverdue && !taskIsDueToday && daysUntilDue !== null && daysUntilDue >= 1 && daysUntilDue <= getAttentionThreshold('DUE_SOON_DAYS', userSettings) && !task.completed && !task.archived,
        flagged: task.flagged && !task.completed && !task.archived,
        aging: daysSinceCreation > getAttentionThreshold('AGING_DAYS', userSettings) && !task.completed && !task.archived,
        stuck: hoursInCurrentStatus !== null && hoursInCurrentStatus > getAttentionThreshold('STUCK_HOURS', userSettings) && !task.completed && !task.archived,
        quickWin: task.effort_level === 1 && task.priority === 'high' && !task.completed && !task.archived
      }

      // Calculate attention score
      let attentionScore = 0
      if (attentionFlags.overdue) attentionScore += 10
      if (attentionFlags.flagged) attentionScore += 8
      if (attentionFlags.dueToday) attentionScore += 7
      if (attentionFlags.stuck) attentionScore += 6
      if (attentionFlags.dueSoon) attentionScore += 4
      if (attentionFlags.aging) attentionScore += 3
      if (attentionFlags.quickWin) attentionScore += 5
      if (task.priority === 'high') attentionScore += 2

      return {
        ...task,
        daysSinceCreation,
        daysUntilDue,
        hoursInCurrentStatus,
        attentionFlags,
        attentionScore,
        needsAttention: attentionScore > 0,
        isCritical: attentionFlags.overdue || attentionFlags.flagged
      }
    })
  }
)

// Critical tasks (high attention score tasks)
export const selectCriticalTasks = createSelector(
  [(state, userTimezone, userSettings) => selectTasksWithAttentionAnalysis(state, userTimezone, userSettings)],
  (attentionTasks) => attentionTasks.filter(task => task.isCritical)
    .sort((a, b) => b.attentionScore - a.attentionScore)
)

// Tasks grouped by attention categories
export const selectTasksByAttentionCategory = createSelector(
  [(state, userTimezone, userSettings) => selectTasksWithAttentionAnalysis(state, userTimezone, userSettings)],
  (attentionTasks) => {
    const categories = {
      overdue: [],
      flagged: [],
      dueToday: [],
      dueSoon: [],
      stuck: [],
      aging: [],
      quickWin: []
    }

    attentionTasks.forEach(task => {
      if (task.attentionFlags.overdue) categories.overdue.push(task)
      if (task.attentionFlags.flagged) categories.flagged.push(task)
      if (task.attentionFlags.dueToday) categories.dueToday.push(task)
      if (task.attentionFlags.dueSoon) categories.dueSoon.push(task)
      if (task.attentionFlags.stuck) categories.stuck.push(task)
      if (task.attentionFlags.aging) categories.aging.push(task)
      if (task.attentionFlags.quickWin) categories.quickWin.push(task)
    })

    // Sort each category by attention score
    Object.keys(categories).forEach(key => {
      categories[key].sort((a, b) => b.attentionScore - a.attentionScore)
    })

    return categories
  }
)

// Top priority tasks for daily focus
export const selectDailyFocusTasks = createSelector(
  [(state, userTimezone, userSettings) => selectTasksWithAttentionAnalysis(state, userTimezone, userSettings)],
  (attentionTasks) => {
    return attentionTasks
      .filter(task => task.needsAttention)
      .sort((a, b) => b.attentionScore - a.attentionScore)
      .slice(0, 10) // Top 10 tasks requiring attention
  }
)

// Projects selectors (moved from projectsSlice)
export const selectProjectsLoading = (state) => state.tasks.loading.projects
export const selectProjectsError = (state) => state.tasks.error.projects
export const selectProjectsInitialized = (state) => state.tasks.initialized.projects

export const selectActiveProjects = createSelector(
  [selectProjects],
  (projects) => projects
    .filter(project => project.status === 'active')
    .sort((a, b) => {
      const aOrder = a.display_order ?? 999999
      const bOrder = b.display_order ?? 999999
      return aOrder - bOrder
    })
)

export const selectProjectById = createSelector(
  [selectProjects, (state, projectId) => projectId],
  (projects, projectId) => projects.find(project => project.id === projectId)
)

export const selectProjectsByGoalId = createSelector(
  [selectProjects, (state, goalId) => goalId],
  (projects, goalId) => projects.filter(project => project.goal_id === goalId)
)

// Realtime selectors
export const selectRealtimeState = (state) => state.tasks.realtime
export const selectRealtimeInitialized = (state) => state.tasks.realtime.initialized
export const selectRealtimeConnectionState = (state) => state.tasks.realtime.connectionState
export const selectRealtimeError = (state) => state.tasks.realtime.error
export const selectRealtimeSubscriptions = (state) => state.tasks.realtime.subscriptions

// Helper selector to check if realtime is connected
export const selectIsRealtimeConnected = createSelector(
  [selectRealtimeConnectionState],
  (connectionState) => connectionState === 'CONNECTED'
)

// Daily Planning selectors
// Smart priority tasks - combines attention scores across all categories
export const selectSmartPriorityTasks = createSelector(
  [(state, userTimezone, userSettings) => selectTasksWithAttentionAnalysis(state, userTimezone, userSettings)],
  (attentionTasks) => {
    return attentionTasks
      .filter(task => !task.completed && !task.archived && task.attentionScore > 0)
      .sort((a, b) => b.attentionScore - a.attentionScore)
  }
)

// Tasks grouped by stream/project
export const selectTasksGroupedByStream = createSelector(
  [selectNonArchivedTasks, selectActiveProjects],
  (tasks, projects) => {
    // Group tasks by project
    const grouped = {}

    // Add tasks to their respective projects
    tasks.forEach(task => {
      const projectId = task.project_id || 'inbox'
      if (!grouped[projectId]) {
        grouped[projectId] = {
          project: projectId === 'inbox' ? null : projects.find(p => p.id === projectId),
          tasks: []
        }
      }
      grouped[projectId].tasks.push(task)
    })

    // Convert to array and sort by project name
    return Object.entries(grouped)
      .map(([projectId, data]) => ({
        projectId,
        projectName: data.project?.name || 'Inbox',
        project: data.project,
        tasks: data.tasks,
        taskCount: data.tasks.length
      }))
      .sort((a, b) => {
        // Inbox last
        if (a.projectId === 'inbox') return 1
        if (b.projectId === 'inbox') return -1
        // Otherwise alphabetical
        return a.projectName.localeCompare(b.projectName)
      })
  }
)

// Helper function to calculate stream health metrics
const calculateStreamHealth = (project, tasks, now) => {
  const projectTasks = tasks.filter(t => t.project_id === project.id)

  if (projectTasks.length === 0) {
    return {
      ...project,
      daysSinceActivity: 0,
      activeTasks: 0,
      totalTasks: 0,
      totalEffort: 0,
      health: 'empty'
    }
  }

  const activeTasks = projectTasks.filter(t => !t.completed && !t.archived)
  const totalEffort = activeTasks.reduce((sum, t) => sum + (t.effort_level || 2), 0)

  // Find most recent activity
  const lastActivity = Math.max(...projectTasks.map(t => {
    const updatedAt = t.updated_at ? new Date(t.updated_at).getTime() : 0
    const createdAt = new Date(t.created_at).getTime()
    return Math.max(updatedAt, createdAt)
  }))

  const daysSinceActivity = Math.max(0, Math.floor((now - lastActivity) / (24 * 60 * 60 * 1000)))

  // Determine health status
  let health
  if (activeTasks.length === 0) {
    health = 'empty'
  } else if (daysSinceActivity < 3) {
    health = 'active'
  } else if (daysSinceActivity < 7) {
    health = 'moderate'
  } else {
    health = 'stale'
  }

  return {
    ...project,
    daysSinceActivity,
    activeTasks: activeTasks.length,
    totalTasks: projectTasks.length,
    totalEffort,
    health
  }
}

// All streams with health indicators
export const selectAllStreamsWithHealth = createSelector(
  [selectActiveProjects, selectNonArchivedTasks],
  (projects, tasks) => {
    const now = Date.now()

    return projects
      .map(project => calculateStreamHealth(project, tasks, now))
      .sort((a, b) => {
        // Sort by health: stale first, then moderate, then active, then empty
        const healthOrder = { stale: 0, moderate: 1, active: 2, empty: 3 }
        return healthOrder[a.health] - healthOrder[b.health]
      })
  }
)

// Stale streams - projects with no activity in 7+ days (kept for backwards compatibility)
export const selectStaleStreams = createSelector(
  [selectAllStreamsWithHealth],
  (streams) => {
    return streams.filter(stream => stream.health === 'stale')
  }
)

// Single project health selector (lightweight - only calculates for one project)
export const selectProjectHealthById = createSelector(
  [selectProjects, selectNonArchivedTasks, (state, projectId) => projectId],
  (projects, tasks, projectId) => {
    if (!projectId) return null
    const project = projects.find(p => p.id === projectId)
    if (!project) return null
    return calculateStreamHealth(project, tasks, Date.now())
  }
)

// Helper function to calculate weekly goal health metrics
const calculateWeeklyGoalHealth = (goal, tasks, now) => {
  const goalTasks = tasks.filter(t => t.weekly_goal_id === goal.id)

  if (goalTasks.length === 0) {
    return {
      ...goal,
      daysSinceActivity: 0,
      activeTasks: 0,
      totalTasks: 0,
      health: 'empty'
    }
  }

  const activeTasks = goalTasks.filter(t => !t.completed && !t.archived)

  // Find most recent activity
  const lastActivity = Math.max(...goalTasks.map(t => {
    const updatedAt = t.updated_at ? new Date(t.updated_at).getTime() : 0
    const createdAt = new Date(t.created_at).getTime()
    return Math.max(updatedAt, createdAt)
  }))

  const daysSinceActivity = Math.max(0, Math.floor((now - lastActivity) / (24 * 60 * 60 * 1000)))

  // Determine health status
  let health
  if (activeTasks.length === 0) {
    health = 'empty'
  } else if (daysSinceActivity < 3) {
    health = 'active'
  } else if (daysSinceActivity < 7) {
    health = 'moderate'
  } else {
    health = 'stale'
  }

  return {
    ...goal,
    daysSinceActivity,
    activeTasks: activeTasks.length,
    totalTasks: goalTasks.length,
    health
  }
}

// All weekly goals with health indicators
export const selectWeeklyGoalsWithHealth = createSelector(
  [state => state.goals.weeklyGoals, state => state.goals.quarterlyGoals, state => state.goals.annualGoals, selectNonArchivedTasks],
  (weeklyGoals, quarterlyGoals, annualGoals, tasks) => {
    const now = Date.now()
    const scheduledGoals = weeklyGoals.filter(goal => goal.status === 'scheduled' && !goal.completed)

    return scheduledGoals.map(goal => {
      const goalWithHealth = calculateWeeklyGoalHealth(goal, tasks, now)

      // Enrich with quarterly and annual goal data (create new objects to avoid mutating frozen Redux state)
      if (goal.quarterly_goal_id) {
        const quarterlyGoal = quarterlyGoals.find(qg => qg.id === goal.quarterly_goal_id)
        if (quarterlyGoal) {
          // Also enrich quarterly goal with annual goal
          let enrichedQuarterlyGoal = quarterlyGoal
          if (quarterlyGoal.annual_goal_id) {
            const annualGoal = annualGoals.find(ag => ag.id === quarterlyGoal.annual_goal_id)
            if (annualGoal) {
              enrichedQuarterlyGoal = {
                ...quarterlyGoal,
                annual_goals: annualGoal
              }
            }
          }

          return {
            ...goalWithHealth,
            quarterly_goals: enrichedQuarterlyGoal
          }
        }
      }

      return goalWithHealth
    })
  }
)

// Single weekly goal health selector (lightweight - only calculates for one goal)
export const selectWeeklyGoalHealthById = createSelector(
  [state => state.goals.weeklyGoals, state => state.goals.quarterlyGoals, state => state.goals.annualGoals, selectNonArchivedTasks, (state, goalId) => goalId],
  (weeklyGoals, quarterlyGoals, annualGoals, tasks, goalId) => {
    if (!goalId) return null
    const goal = weeklyGoals.find(g => g.id === goalId)
    if (!goal) return null

    const goalWithHealth = calculateWeeklyGoalHealth(goal, tasks, Date.now())

    // Enrich with quarterly and annual goal data
    if (goal.quarterly_goal_id) {
      const quarterlyGoal = quarterlyGoals.find(qg => qg.id === goal.quarterly_goal_id)
      if (quarterlyGoal) {
        let enrichedQuarterlyGoal = quarterlyGoal
        if (quarterlyGoal.annual_goal_id) {
          const annualGoal = annualGoals.find(ag => ag.id === quarterlyGoal.annual_goal_id)
          if (annualGoal) {
            enrichedQuarterlyGoal = {
              ...quarterlyGoal,
              annual_goals: annualGoal
            }
          }
        }
        return {
          ...goalWithHealth,
          quarterly_goals: enrichedQuarterlyGoal
        }
      }
    }

    return goalWithHealth
  }
)

/**
 * Converts a UTC timestamp to a date string in the user's timezone
 * Uses Intl.DateTimeFormat for reliable timezone conversion (avoids string parsing bugs)
 * @param {string} timestamp - UTC timestamp from database
 * @param {string} timezone - User's timezone (e.g., "America/New_York")
 * @returns {string|null} - Date string in YYYY-MM-DD format or null if invalid
 */
const getDateStringInTimezone = (timestamp, timezone) => {
  if (!timestamp) return null
  try {
    const date = new Date(timestamp)
    if (isNaN(date.getTime())) return null

    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    // en-CA locale formats as YYYY-MM-DD
    return formatter.format(date)
  } catch (error) {
    console.error('Error converting timestamp to timezone:', error)
    return null
  }
}

// Get completed tasks by date (for yesterday's review)
export const selectCompletedTasksByDate = createSelector(
  [
    selectTasks,
    (state, dateString) => dateString,
    (state, dateString, timezone) => timezone || DEFAULT_TIMEZONE
  ],
  (tasks, dateString, timezone) => {
    if (!dateString) return []

    // Get completed tasks and filter by date
    const completedTasks = tasks.filter(task => task.completed)

    // Filter tasks by completion date in user's timezone
    const tasksForDate = completedTasks.filter(task => {
      const timestamp = task.completed_at || task.updated_at
      if (!timestamp) return false

      const taskDateStr = getDateStringInTimezone(timestamp, timezone)
      return taskDateStr === dateString
    })

    return tasksForDate.sort((a, b) => {
      const aTime = new Date(a.completed_at || a.updated_at).getTime()
      const bTime = new Date(b.completed_at || b.updated_at).getTime()
      return bTime - aTime
    })
  }
)

// Get completed tasks counts by date for a date range (for calendar indicators)
export const selectCompletedTasksCountsByDateRange = createSelector(
  [
    selectTasks,
    (state, startDate) => startDate,
    (state, startDate, endDate) => endDate,
    (state, startDate, endDate, timezone) => timezone || DEFAULT_TIMEZONE
  ],
  (tasks, startDate, endDate, timezone) => {
    if (!startDate || !endDate) return new Map()

    // Get all completed tasks
    const completedTasks = tasks.filter(task => task.completed)

    // Build a map of date -> count
    const countsMap = new Map()

    completedTasks.forEach(task => {
      const timestamp = task.completed_at || task.updated_at
      if (!timestamp) return

      const taskDateStr = getDateStringInTimezone(timestamp, timezone)
      if (!taskDateStr) return

      // Only include dates within the range
      if (taskDateStr >= startDate && taskDateStr <= endDate) {
        countsMap.set(taskDateStr, (countsMap.get(taskDateStr) || 0) + 1)
      }
    })

    return countsMap
  }
)

export default tasksSlice.reducer
